import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV_LINKS, PRODUCT_MENU, COMPANY } from "@/lib/site";
import { trackEvent } from "@/lib/analytics";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const isProductPage = pathname.startsWith("/produk/");
  const onDarkIntro = pathname === "/" || pathname.startsWith("/produk/");
  const darkHero = onDarkIntro && !scrolled;
  const headerHeroClass = pathname === "/produk/elektronik"
    ? "bg-[#050506]/88"
    : pathname === "/produk/kitchen-equipment"
      ? "bg-[#11100f]/88"
      : pathname === "/produk/animax-acrylic-sheet"
        ? "bg-[#17130d]/88"
        : pathname === "/produk/baja-ringan-galvalum"
          ? "bg-[#07172f]/88"
          : "bg-[#06142e]/82";
  const getNavHref = (href) => href;
  const isActive = (href) => {
    if (href === "/") return pathname === "/";
    if (href.includes("#produk")) return pathname.startsWith("/produk/");
    return pathname === href;
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        darkHero
            ? `${headerHeroClass} backdrop-blur-xl border-b border-white/10 py-2.5 shadow-[0_12px_35px_rgba(0,0,0,0.18)]`
            : scrolled
            ? "glass-light py-1.5 sm:py-2 border-b border-[#071c4c]/5"
            : "bg-transparent py-3 sm:py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 flex items-center justify-between min-w-0">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-4 group shrink-0 mr-3 sm:mr-6 rounded-xl focus-visible:outline-none">
          <img
            src={COMPANY.logos.aplTransparent}
            alt="PT Animax Putra Lancar"
            className={`object-contain transition-all duration-500 group-hover:scale-[1.03] ${
              darkHero
                ? "h-11 w-11 sm:h-12 sm:w-12 rounded-xl bg-white p-1 shadow-lg shadow-black/20"
                : scrolled
                  ? "h-10 w-10 sm:h-11 sm:w-11"
                  : "h-12 w-12 sm:h-16 sm:w-16"
            }`}
          />
          <div className="leading-tight hidden md:block">
            <div
              className={`font-display font-semibold text-xl lg:text-2xl tracking-tight transition-colors ${
                darkHero ? "text-white" : "text-animax-black"
              }`}
            >
              PT. Animax Putra Lancar
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((link) =>
            link.href.includes("#produk") ? (
              <div key={link.href} className="relative group">
                <a
                  href={link.href}
                  data-testid="nav-link-produk"
                  className={`inline-flex items-center gap-1 text-sm transition-colors relative ${
                    darkHero ? "text-white/75 hover:text-white" : "text-neutral-700 hover:text-animax-black"
                  } ${isActive(link.href) ? `font-semibold ${darkHero ? "text-white" : "text-animax-black"}` : ""}`}
                >
                  {link.label}
                  <ChevronDown className="w-3.5 h-3.5 group-hover:rotate-180 transition-transform" />
                </a>
                <div
                  data-testid="nav-product-dropdown"
                  className="absolute left-0 top-full pt-3 hidden group-hover:block"
                >
                  <div className="w-56 rounded-xl glass-light border border-neutral-200 shadow-xl p-2">
                    {PRODUCT_MENU.map((p, i) =>
                      p.available ? (
                        <Link
                          key={i}
                          to={p.href}
                          data-testid={`nav-product-item-${i}`}
                          onClick={() =>
                            trackEvent("click_product_nav", {
                              category: "product",
                              label: p.label,
                              product_name: p.analyticsName || p.label,
                            })
                          }
                          className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-animax-black hover:bg-animax-yellow/20 transition-colors"
                        >
                          {p.label}
                        </Link>
                      ) : (
                        <div
                          key={i}
                          data-testid={`nav-product-item-${i}`}
                          className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-neutral-400 cursor-default"
                        >
                          {p.label}
                          <span className="text-[10px] tracking-widest uppercase font-medium text-neutral-400">Segera</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <a
                key={link.href}
                href={getNavHref(link.href)}
                data-testid={`nav-link-${link.href.replace(/[\/#]/g, "")}`}
                className={`text-sm transition-colors relative group ${
                  darkHero ? "text-white/75 hover:text-white" : "text-neutral-700 hover:text-animax-black"
                }`}
              >
                {link.label}
                  <span className={`absolute -bottom-1 left-0 h-px ${isProductPage ? "bg-animax-yellow" : "bg-gradient-to-r from-[#e60012] to-[#ff6873]"} transition-all duration-300 ${isActive(link.href) ? "w-full" : "w-0 group-hover:w-full"}`} />
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-3 shrink-0 ml-3 sm:ml-6">
          <a
            href="/kontak#kontak"
            data-testid="nav-cta-sales"
            className={`hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 shadow-sm ${
              darkHero
                ? "bg-white text-animax-black hover:bg-animax-yellow"
                : "bg-animax-black text-white hover:bg-animax-charcoal"
            }`}
          >
            Hubungi Kami
          </a>
          <button
            data-testid="nav-mobile-toggle"
            className={`lg:hidden p-2 rounded-md transition-colors ${
              darkHero ? "text-white hover:bg-white/10" : "hover:bg-neutral-100"
            }`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden mt-3 mx-5 sm:mx-6 rounded-2xl glass-light p-5 sm:p-6 space-y-3" data-testid="mobile-menu">
          {NAV_LINKS.map((link) => (
            <div key={link.href} className="border-b border-neutral-200 last:border-0">
              <a
                href={getNavHref(link.href)}
                onClick={() => setOpen(false)}
                className="block text-sm text-neutral-700 hover:text-animax-orange py-2"
                data-testid={`mobile-nav-link-${link.href.replace(/[\/#]/g, "")}`}
              >
                {link.label}
              </a>
              {link.href.includes("#produk") && (
                <div className="pl-4 pb-2 space-y-1">
                  {PRODUCT_MENU.map((p, i) =>
                    p.available ? (
                      <Link
                        key={i}
                        to={p.href}
                        onClick={() => {
                          trackEvent("click_product_nav", {
                            category: "product",
                            label: p.label,
                            product_name: p.analyticsName || p.label,
                          });
                          setOpen(false);
                        }}
                        className="block text-xs text-neutral-600 hover:text-animax-orange py-1">
                        {p.label}
                      </Link>
                    ) : (
                      <div key={i} className="text-xs text-neutral-400 py-1">{p.label} · Segera</div>
                    )
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </header>
  );
};
